import React from "react";
import { DayPicker, DayPickerRangeController } from "react-dates";
import { START_DATE } from "react-dates/constants";
import CSSModules from "lib/utils/css-modules";

import {
  st,
  Label,
  Form,
  FormControl,
  Message,
  Accordion,
  Menu,
  StockAvailability,
  ProductScheduleSlotDecorator,
  OrderFullDecorator,
} from "lib/index";
import SlotTimeFormSection, {
  handleSlotTimePropChange,
} from "modules/products/EditProductView/StepAvailability/SlotTimeFormSection";
import styles from "./OrderScheduleForm.less";

class CustomSlotFormSection extends React.PureComponent {
  handleSlotPropChange(prop, value) {
    const { value: slot, onChange } = this.props;
    onChange(
      new ProductScheduleSlotDecorator({
        ...slot,
        [prop]: value,
      })
    );
  }

  render() {
    const { name, value: slot, errors, onChange, onValidate, showAllAttributes = true } = this.props;
    return (
      <Form onSubmit={(event) => event.preventDefault()}>
        <div className="ui field">
          <SlotTimeFormSection name={name} errors={errors} value={slot} onBlur={onValidate} onChange={onChange} />
        </div>

        {showAllAttributes && (
          <FormControl.CheckboxFormGroup
            {...FormControl.addProps(`${name}.active`, { errors })}
            value={slot.active}
            onBlur={onValidate}
            onChange={this.handleSlotPropChange.bind(this, "active")}
            label={I18n.humanAttributeName("product_schedule_slot", "active")}
            toggle
          />
        )}

        {showAllAttributes && (
          <FormControl.NumberLimitFormGroup
            {...FormControl.addProps(`${name}.total_stock_limit`, { errors })}
            value={slot.total_stock_limit}
            onBlur={onValidate}
            onChange={this.handleSlotPropChange.bind(this, "total_stock_limit")}
            label={I18n.humanAttributeName("product_schedule_slot", "total_stock_limit")}
            inlineLabel
            min={1}
          />
        )}

        <FormControl.CheckboxFormGroup
          {...FormControl.addProps(`${name}.availability_type`, { errors })}
          checked={slot.availability_type === "available_with_instant_booking"}
          onBlur={onValidate}
          onChange={(checked) =>
            this.handleSlotPropChange(
              "availability_type",
              checked ? "available_with_instant_booking" : "available_with_company_approval"
            )
          }
          label={I18n.humanAttributeName("product_schedule_slot", "available_with_instant_booking")}
          help={
            slot.availability_type === "available_with_instant_booking"
              ? I18n.humanAttributeName("product_schedule_slot", "available_with_instant_booking_help")
              : I18n.humanAttributeName("product_schedule_slot", "available_with_company_approval_help")
          }
          toggle
        />

        {showAllAttributes && (
          <FormControl.TextAreaFormGroup
            {...FormControl.addProps(`${name}.note`, { errors })}
            value={slot.note}
            onBlur={onValidate}
            onChange={this.handleSlotPropChange.bind(this, "note")}
            placeholder={I18n.humanAttributeName("product_schedule_slot", "note")}
            autoHeight
          />
        )}
      </Form>
    );
  }
}

@CSSModules(styles)
export default class OrderScheduleForm extends React.PureComponent {
  constructor(...args) {
    super(...args);
    const { order, initialSelectedDate } = this.props;
    const initialSelectedDateKey =
      initialSelectedDate ||
      (order && order.product_schedule_slot
        ? moment.parseZone(order.product_schedule_slot.starts_at).format(moment.RUBY_DATE_FORMAT)
        : null);
    this.state = {
      selectedDateKey: initialSelectedDateKey,
    };
  }

  componentDidMount() {
    const { order } = this.props;
    if (!order.product_variant) {
      this.handleUseCustomSlotChange(true);
    }
  }

  handleDayClick(date) {
    const dateKey = date.format(moment.RUBY_DATE_FORMAT);
    const dates = this._getSlotStatusesByDate();
    const nextSelectedDateKey = dates[dateKey] ? dateKey : null;
    this.setState({ selectedDateKey: nextSelectedDateKey });
    // const nextSlot = this._getSlotsForSelectedDate(nextSelectedDateKey)[0]
    // this.handleSlotChange(nextSlot)
  }

  handleUseCustomSlotChange(useCustomSlot) {
    const { order, onOrderUpdate } = this.props;
    if (useCustomSlot) {
      const nextOrder = new OrderFullDecorator({
        ...order,
        product_schedule_slot: ProductScheduleSlotDecorator.build({
          company_id: order.company.id,
          product_id: order.product && order.product.id,
          product_variant_id: order.product_variant && order.product_variant.id,
          time_zone_name: order.company.time_zone_name,
          // TODO if its agent, different availability_type
          availability_type: "available_with_instant_booking",
          stock_type: order.product_variant ? order.product_variant.stock_type : order.stock_type,
          product_name: order.product_name,
          product_variant_name: order.product_variant_name,
        }),
      });
      nextOrder.cacheSlotAttributes();
      onOrderUpdate(nextOrder);
    } else {
      const nextOrder = new OrderFullDecorator({
        ...order,
        product_schedule_slot: null,
      });
      nextOrder.cacheSlotAttributes();
      onOrderUpdate(nextOrder);
      this.setState({
        selectedDateKey: null,
      });
    }
  }

  handleSlotChange(product_schedule_slot) {
    const { order, onOrderUpdate } = this.props;
    const nextOrder = new OrderFullDecorator({
      ...order,
      product_schedule_slot,
    });
    nextOrder.cacheSlotAttributes();
    onOrderUpdate(nextOrder);
  }

  handleSlotStartDateChange(date) {
    const {
      order: { product_schedule_slot: slot },
    } = this.props;
    const nextSlot = handleSlotTimePropChange(slot, "start_date", date && date.format(moment.RUBY_DATE_FORMAT));
    this.handleSlotChange(nextSlot);
  }

  handleSlotSelect(product_schedule_slot) {
    const { order, onOrderUpdate, onSave, autosaveOnSlotSelect = true } = this.props;
    this.handleSlotChange(product_schedule_slot);
    autosaveOnSlotSelect && onSave();
  }

  _getSlotStatusesByDate() {
    const { slots } = this.props;
    const dates = {};
    slots.forEach((slot) => {
      const key = slot.start_date;
      dates[key] = dates[key] || (slot.orderable ? "available" : "busy");
      dates[key] = slot.orderable ? "available" : dates[key];
    });
    return dates;
  }

  _getSlotsForSelectedDate(selectedDateKey = this.state.selectedDateKey) {
    const { slots } = this.props;
    if (selectedDateKey) {
      return slots.filter((sl) => sl.start_date === selectedDateKey);
    } else {
      return slots.filter((sl) => sl.orderable).slice(0, 5);
    }
  }

  getInitialVisibleMonth() {
    const { order, slots } = this.props;
    const firstOrderableSlot = slots.filter((sl) => sl.orderable)[0];
    return firstOrderableSlot ? firstOrderableSlot.starts_at_moment : moment.tz(order.company_time_zone_name);
  }

  render() {
    const { order, slots, errors, valid, onValidate, canCreateCustomSlot } = this.props;
    const { selectedDateKey } = this.state;
    const dates = this._getSlotStatusesByDate();
    const slotsForSelectedDate = this._getSlotsForSelectedDate();
    const slot = order.product_schedule_slot;
    const usesCustomSlot = slot && !slot.id;
    const firstOrderableSlot = slots.filter((sl) => sl.orderable)[0];

    return (
      <div styleName="columns">
        <div styleName="column-calendar">
          <div className="DayPickerNoPaddingContainer">
            {!usesCustomSlot && (
              <DayPicker
                initialVisibleMonth={this.getInitialVisibleMonth.bind(this)}
                onDayClick={this.handleDayClick.bind(this)}
                modifiers={{
                  past: (day) => day.isBefore(),
                  today: (day) => day.isSame(undefined, "days"),
                  blocked: (day) => !dates[day.format(moment.RUBY_DATE_FORMAT)],
                  available: (day) => dates[day.format(moment.RUBY_DATE_FORMAT)] === "available",
                  busy: (day) => dates[day.format(moment.RUBY_DATE_FORMAT)] === "busy",
                  focused: (day) => selectedDateKey === day.format(moment.RUBY_DATE_FORMAT),
                  "selected-span": (day) => slot && slot.start_date && day.isSame(slot.start_date, "day"),
                }}
              />
            )}
            {usesCustomSlot && (
              <DayPicker
                onDayClick={this.handleSlotStartDateChange.bind(this)}
                modifiers={{
                  past: (day) => day.isBefore(),
                  today: (day) => day.isSame(undefined, "days"),
                  "selected-start": (day) => slot && slot.start_date && day.isSame(slot.start_date, "day"),
                  "selected-span": (day) =>
                    slot &&
                    slot.start_date &&
                    slot.end_date &&
                    day.isBetween(slot.start_date, slot.end_date, "day", "()"),
                  "selected-end": (day) => slot && slot.end_date && day.isSame(slot.end_date, "day"),
                }}
              />
            )}
          </div>
        </div>

        <div styleName="column-dates">
          <Accordion styled exclusive>
            <Accordion.Title
              hidden={!order.product}
              active={!usesCustomSlot}
              onClick={this.handleUseCustomSlotChange.bind(this, !usesCustomSlot)}
              className="u-cursorDefault"
            >
              {I18n.t("papp.orders.OrderScheduleForm.select_existing_slot")} <i className="icon dropdown" />
            </Accordion.Title>
            <Accordion.Content hidden={!order.product} active={!usesCustomSlot}>
              <h4>
                {selectedDateKey
                  ? st.capitalize(moment(selectedDateKey).format("dddd, DD MMMM"))
                  : I18n.t("papp.orders.OrderScheduleForm.upcoming_slots")}
              </h4>

              {slotsForSelectedDate.length === 0 && slots.filter((sl) => sl.orderable).length === 0 && (
                <Message>{I18n.t("papp.orders.OrderScheduleForm.no_slots_found")}</Message>
              )}

              {slotsForSelectedDate.length > 0 && (
                <Menu vertical fluid className="c-menuWithHighlightedActiveItem">
                  {slotsForSelectedDate.map((sl) => (
                    <Menu.Item
                      key={sl.uuid}
                      onClick={this.handleSlotSelect.bind(this, sl)}
                      active={slot && slot.uuid === sl.uuid}
                    >
                      <Label color={sl.overbooked ? "red" : undefined}>
                        <StockAvailability slot={sl} overbooked={false} />
                      </Label>
                      {selectedDateKey
                        ? moment
                            .parseZone(sl.starts_at)
                            .format(
                              sl.includes_time ? moment.TIME_FORMAT : I18n.t("papp.orders.OrderScheduleForm.all_day")
                            )
                        : moment.parseZone(sl.starts_at).format(sl.includes_time ? "DD MMM, HH:mm" : "DD MMM")}
                    </Menu.Item>
                  ))}
                </Menu>
              )}
            </Accordion.Content>

            {canCreateCustomSlot && [
              <Accordion.Title
                key="custom-slot-title"
                active={usesCustomSlot}
                onClick={this.handleUseCustomSlotChange.bind(this, !usesCustomSlot)}
              >
                {I18n.t("papp.orders.OrderScheduleForm.add_new_slot")} <i className="icon dropdown" />
              </Accordion.Title>,
              <Accordion.Content key="custom-slot-content" active={usesCustomSlot}>
                {errors.base && (
                  <Message error visible>
                    {errors.base}
                  </Message>
                )}
                {usesCustomSlot && (
                  <CustomSlotFormSection
                    name={`order.product_schedule_slot`}
                    errors={errors}
                    value={slot}
                    onChange={this.handleSlotChange.bind(this)}
                    onBlur={onValidate}
                    showAllAttributes={!!order.product_variant}
                  />
                )}
              </Accordion.Content>,
            ]}
          </Accordion>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/orders/OrderScheduleForm/OrderScheduleForm.jsx